import { useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router'
import Dialog from '@mui/material/Dialog'
import styles from './Compose.pcss'
import {
  addImagePosts,
  getLinkDetails,
  resetComposer,
  setComposerBulkUploadFileUrl,
  setComposerImages,
  setComposerStatus
} from 'services/compose'
import { useDispatch, useSelector } from 'react-redux'
import { composerIsEmptySelector } from 'services/compose/selectors'
import Composer from 'components/Composer'
import { AIWriter } from 'components/AIWriter'
import { parse } from 'query-string'
import { StoreThunkDispatch } from 'store/state'
import { COMPOSER_STATUS_KEY_GENERIC } from 'services/compose/state'

declare const __DEV__: boolean

export function Compose() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const isEmpty = useSelector(composerIsEmptySelector)
  const prevLocationState = useRef<any>({})

  const query = parse(location.search)
  const { shareUrl, shareImageUrl, shareImageUrls, imageSource, multi } = query

  useEffect(() => {
    if (typeof shareUrl === 'string') {
      const url = decodeURIComponent(shareUrl)
      dispatch(resetComposer())
      dispatch(getLinkDetails({ url }))
      dispatch(setComposerStatus({ text: url, html: url, network: COMPOSER_STATUS_KEY_GENERIC }))
      navigate('/composer', { replace: true })
    } else if (typeof shareImageUrl === 'string') {
      const imgUrl = decodeURIComponent(shareImageUrl)
      dispatch(setComposerImages([imgUrl]))
      if (typeof imageSource === 'string') {
        const status = `Image source: ${imageSource}`
        dispatch(setComposerStatus({ text: status, html: status, network: COMPOSER_STATUS_KEY_GENERIC }))
      }
      navigate('/composer', { replace: true })
    } else if (typeof shareImageUrls === 'string') {
      // NOTE: backward compatibility for PP extension v1 where separator was ','
      const separator = shareImageUrls.includes('|') ? '|' : ','
      const urls = decodeURIComponent(shareImageUrls).split(separator).filter(Boolean)
      if (multi) {
        dispatch(addImagePosts(urls.map(url => ({ url }))))
      } else {
        dispatch(setComposerImages(urls))
      }
      navigate('/composer', { replace: true })
    }
  }, [shareUrl, shareImageUrl, imageSource, shareImageUrls, multi, dispatch, navigate])

  useEffect(() => {
    if (location.state?.opener !== '/composer') {
      prevLocationState.current = location.state
    }
  }, [location.state])

  const onUnload = useCallback((event: Event) => {
    event.preventDefault()
    event.returnValue = false
    return ''
  }, [])

  useEffect(() => {
    if (__DEV__) {
      return
    }
    if (!isEmpty) {
      window.addEventListener('beforeunload', onUnload)
    }
    return () => {
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [isEmpty, onUnload])

  const close = (reset?: boolean, redirectUrl?: string) => {
    if (redirectUrl) {
      navigate(redirectUrl)
    } else if (prevLocationState.current) {
      const { opener, search, nextState } = prevLocationState.current
      navigate(opener || '/', { state: { search, ...nextState } })
    } else {
      navigate('/')
    }

    dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
    if (reset) {
      dispatch(resetComposer())
    }
  }

  return (
    <Dialog
      open
      fullScreen
      disableEnforceFocus
      classes={{ root: styles.dialog, paper: styles.paper }}
      data-testid="composer-dialog"
    >
      <Composer onClose={close} />
      <AIWriter />
    </Dialog>
  )
}

export default Compose
